import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { faCalendar, IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { Button, Flex } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import isNil from 'lodash.isnil'
import styled from 'styled-components'

import useDisclosure from 'src/hooks/use-disclosure'

import { TimeZone } from '../../../../types/graphql'
import Text from '../../../components/Typography/Text'
import { KeyboardKey } from '../../../types'
import { convertDateToTimeZone, DateFormats, getDayjsInTimeZone } from '../../../utils/date'
import { formatNumber, NumberFormat } from '../../../utils/number'
import { Size } from '../../types'
import Calendar from '../Calendar/Calendar'
import { FormControlProps } from '../FormControl/util'
import Icon from '../Icon/Icon'
import Input from '../Input/Input'
import NumberInput from '../Input/NumberInput'
import { MenuContentStyles } from '../Menu'
import Popover from '../Popover/Popover'

import { formatDateForInputText, getIsStringCompleteDate } from './util'

const DatePickerPopoverContent = styled(Popover.Content)`
  ${MenuContentStyles};
  padding-bottom: 8px;
`

export enum DatePickerAllowedDates {
  Future = 'future',
  All = 'all',
}

type Props = Omit<FormControlProps, 'isLabelRedundant'> & {
  format?: DateFormats
  getIsDateDisabled?: (date: Dayjs) => boolean
  isClearable?: boolean
  isDisabled?: boolean
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onValueChange: (value: Dayjs | null) => void
  placeholder?: string
  size?: Size
  suffixIcon?: IconDefinition | null
  textAlign?: Extract<React.CSSProperties['textAlign'], 'left' | 'center'>
  timeZone?: TimeZone | null
  value: Dayjs | null | undefined
}
const DatePicker = ({
  ariaLabel,
  caption,
  errorMessage,
  format,
  getIsDateDisabled: propGetIsDateDisabled,
  id,
  isClearable,
  isDisabled,
  isInvalid,
  isRequired,
  label,
  onBlur,
  onValueChange,
  placeholder,
  size,
  suffixIcon = faCalendar,
  textAlign = 'left',
  timeZone,
  tooltip,
  value,
  width,
}: Props) => {
  const popoverState = useDisclosure()
  const popoverContentRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const weekdaysButtonRef = useRef<HTMLButtonElement>(null)
  const weekdaysInputRef = useRef<HTMLInputElement>(null)

  const {
    isOpen: isEditingWeekdays,
    open: startEditingWeekdays,
    close: stopEditingWeekdays,
  } = useDisclosure()

  const handleEditWeekdaysClicked = useCallback(() => {
    startEditingWeekdays()
    requestAnimationFrame(() => {
      weekdaysInputRef.current?.focus()
    })
  }, [startEditingWeekdays])

  const handleClose = useCallback(() => {
    popoverState.close()
    stopEditingWeekdays()
  }, [popoverState, stopEditingWeekdays])

  const numberOfWeekdays = useMemo(() => {
    const nowInTimeZone = getDayjsInTimeZone(dayjs(), timeZone).startOf('day')
    return value ? dayjs(value).startOf('day').businessDiff(nowInTimeZone) : undefined
  }, [value, timeZone])
  const [inputNumberOfWeekdays, setInputNumberOfWeekdays] = useState(numberOfWeekdays)
  const maxWeekdays = 1000
  const minWeekdays = -1000
  const isNumberOfWeekdaysOutOfRange =
    !isNil(inputNumberOfWeekdays) &&
    (inputNumberOfWeekdays > maxWeekdays || inputNumberOfWeekdays < minWeekdays)

  const defaultInputText = useMemo(
    () =>
      formatDateForInputText(value, {
        format,
      }),
    [format, value]
  )
  const [inputText, setInputText] = useState(defaultInputText) // The user's temporary text entry
  const defaultMonth = useMemo(() => value ?? dayjs(), [value])
  const [month, setMonth] = useState(defaultMonth) // The month which is being viewed in the popover calendar

  const selectedDate = useMemo(() => value?.toDate(), [value])
  const selectedMonth = useMemo(() => month.toDate(), [month])

  const { isOpen: hasFocus, open: focus, close: blur } = useDisclosure()

  useEffect(() => {
    if (!hasFocus) {
      // If the prop value changes while the input does not have focus, reset the input
      setInputText(defaultInputText)
    }
  }, [defaultInputText, hasFocus])

  const getIsDateDisabled = useCallback(
    (date: Date) => {
      return propGetIsDateDisabled?.(dayjs(date)) ?? false
    },
    [propGetIsDateDisabled]
  )

  const handleValueChange = useCallback(
    (newValue: Dayjs | null, resetInputText: boolean = true) => {
      if (newValue && getIsDateDisabled(newValue.toDate())) {
        return
      }
      onValueChange(convertDateToTimeZone(newValue, timeZone))
      if (newValue) {
        setMonth(newValue)
      }
      if (resetInputText) {
        setInputText(formatDateForInputText(newValue, { format }))
      }
    },
    [format, getIsDateDisabled, onValueChange, timeZone]
  )

  const handleInputTextChanged = useCallback(
    (newValue: string | undefined) => {
      if (!newValue) {
        setInputText('')
        if (isClearable) {
          handleValueChange(null)
        }
        return
      }

      setInputText(newValue)

      if (getIsStringCompleteDate(newValue)) {
        // don't reset the input text as the user is still typing
        handleValueChange(dayjs(newValue), false)
      }
    },
    [handleValueChange, isClearable]
  )

  const handleOpen = useCallback(() => {
    if (!popoverState.isOpen) {
      setMonth(defaultMonth)
      popoverState.open()
    }
  }, [defaultMonth, popoverState])

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === KeyboardKey.ArrowDown) {
        e.preventDefault()
        handleOpen()
        requestAnimationFrame(() => {
          popoverContentRef.current?.focus()
        })
      }
    },
    [handleOpen]
  )

  const handleInputClicked = useCallback(() => {
    handleOpen()
  }, [handleOpen])

  const handleInputFocused = useCallback(() => {
    focus()
    handleOpen()
  }, [focus, handleOpen])

  const handleInputBlurred = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (getIsStringCompleteDate(inputText)) {
        handleValueChange(dayjs(inputText))
      } else {
        setInputText(defaultInputText)
      }

      blur()
      setTimeout(() => {
        // Timeout needed to skip render cycle for activeElement to update (where focus is)
        if (!popoverContentRef.current?.contains(document.activeElement)) {
          // Don't close the popover if it gained focus
          handleClose()
          onBlur?.(e)
        }
      })
    },
    [blur, defaultInputText, handleClose, handleValueChange, inputText, onBlur]
  )

  const handleInteractOutsidePopover = useCallback(
    (
      e: Parameters<
        NonNullable<React.ComponentProps<typeof Popover.Content>['onInteractOutside']>
      >[0]
    ) => {
      if (hasFocus) {
        e.preventDefault()
      }
    },
    [hasFocus]
  )

  const handleMonthChanged = useCallback((newValue: Date | undefined) => {
    setMonth(newValue ? dayjs(newValue) : dayjs())
  }, [])

  const handleCalendarDateSelected = useCallback(
    (newValue: Date | undefined) => {
      if (!newValue) {
        return
      }
      const newDateValue = dayjs(newValue)
      handleValueChange(newDateValue)
      handleClose()
      inputRef.current?.focus()
    },
    [handleClose, handleValueChange]
  )

  const handleTodayClicked = useCallback(() => {
    if (getIsDateDisabled(new Date())) {
      return
    }
    handleCalendarDateSelected(new Date())
  }, [getIsDateDisabled, handleCalendarDateSelected])

  const weekdaysLabel = useMemo(() => {
    if (isNil(inputNumberOfWeekdays)) {
      return ' weekdays in the future'
    }
    if (inputNumberOfWeekdays >= 0) {
      return ` ${inputNumberOfWeekdays === 1 ? 'weekday' : 'weekdays'} in the future`
    } else {
      return ` ${inputNumberOfWeekdays === -1 ? 'weekday' : 'weekdays'} in the past`
    }
  }, [inputNumberOfWeekdays])

  const weekdaysButtonLabel = useMemo(() => {
    if (isNil(inputNumberOfWeekdays)) {
      return 'Select # of weekdays'
    }
    return `${formatNumber(Math.abs(inputNumberOfWeekdays), {
      format: NumberFormat.Integer,
    })}${weekdaysLabel}`
  }, [inputNumberOfWeekdays, weekdaysLabel])

  useEffect(() => {
    setInputNumberOfWeekdays(numberOfWeekdays)
  }, [numberOfWeekdays])
  const handleInputNumberOfWeekdaysChange = useCallback(
    (newNumberOfWeekdays: number | undefined) => {
      setInputNumberOfWeekdays(newNumberOfWeekdays)
      if (isNil(newNumberOfWeekdays)) {
        // Don't clear out the date value
        return
      }
      const today = dayjs().startOf('day')
      const newValue = today.businessDaysAdd(newNumberOfWeekdays)
      handleValueChange(newValue)
    },
    [handleValueChange]
  )

  const saveWeekdaysInput = useCallback(() => {
    stopEditingWeekdays()
    setInputNumberOfWeekdays(numberOfWeekdays)
  }, [numberOfWeekdays, stopEditingWeekdays])

  const handleNumberOfWeekdaysInputBlur = useCallback(() => {
    saveWeekdaysInput()
  }, [saveWeekdaysInput])

  const handleNumberOfWeekdaysInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === KeyboardKey.Enter || e.key === KeyboardKey.Escape) {
        e.preventDefault()
        saveWeekdaysInput()
        requestAnimationFrame(() => {
          weekdaysButtonRef.current?.focus()
        })
      }
    },
    [saveWeekdaysInput]
  )

  const handlePopoverEscapeKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (weekdaysInputRef.current === document.activeElement) {
        e.preventDefault()
        saveWeekdaysInput()
        requestAnimationFrame(() => {
          weekdaysButtonRef.current?.focus()
        })
        return
      }
      inputRef.current?.focus()
    },
    [saveWeekdaysInput]
  )

  const leftAlignOffset = -12 // nice value for left-alignment of the input box
  const [alignOffset, setAlignOffset] = useState(leftAlignOffset)
  useEffect(() => {
    if (textAlign === 'left' || !inputRef.current) {
      setAlignOffset(leftAlignOffset)
      return
    }

    // Compute left-alignment with the input /text/ which is center-aligned
    const span = createTextWidthSpan(inputRef.current)

    const inputTextWidth = span.clientWidth
    const inputWidth = inputRef.current.clientWidth
    setAlignOffset(Math.max((inputWidth - inputTextWidth) / 2 - 16, leftAlignOffset))
  }, [textAlign, value, inputText, leftAlignOffset])

  const endMonth = useMemo(() => dayjs().add(20, 'year').toDate(), [])

  return (
    <Popover isOpen={popoverState.isOpen} onOpen={handleOpen} onClose={handleClose}>
      <Popover.Anchor asChild>
        <Input
          {...(suffixIcon ? { suffixIcon } : {})}
          {...(label ? { label } : { ariaLabel: ariaLabel || '' })}
          caption={caption}
          errorMessage={errorMessage}
          id={id}
          isClearable={isClearable}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          isRequired={isRequired}
          tooltip={tooltip}
          onBlur={handleInputBlurred}
          onClick={handleInputClicked}
          onFocus={handleInputFocused}
          onKeyDown={handleInputKeyDown}
          onValueChange={handleInputTextChanged}
          placeholder={placeholder ?? 'Select a date'}
          ref={inputRef}
          size={size}
          textAlign={textAlign}
          value={inputText}
          width={width}
        />
      </Popover.Anchor>
      <DatePickerPopoverContent
        align="start"
        alignOffset={alignOffset}
        autoFocus={false}
        sideOffset={size === 'sm' ? 6 : size === 'md' ? 8 : 12}
        onEscapeKeyDown={handlePopoverEscapeKeyDown}
        onFocusOutside={(e) => e.preventDefault()}
        onInteractOutside={handleInteractOutsidePopover}
        onOpenAutoFocus={(e) => e.preventDefault()}
        ref={popoverContentRef}
      >
        <Calendar
          captionLayout="dropdown-years"
          disabled={getIsDateDisabled}
          endMonth={endMonth}
          mode="single"
          month={selectedMonth}
          onMonthChange={handleMonthChanged}
          onSelect={handleCalendarDateSelected}
          selected={selectedDate}
        />
        <Flex vertical align="center" justify="center">
          <Button type="link" onClick={handleTodayClicked} icon={<Icon.FaCalendarDay />}>
            Today
          </Button>
          {isEditingWeekdays ? (
            <Flex align="center" gap="8px">
              <NumberInput
                ariaLabel="Number of weekdays"
                allowNegative
                format={NumberFormat.Integer}
                leftAddon={null}
                max={1000}
                min={-1000}
                onBlur={handleNumberOfWeekdaysInputBlur}
                onKeyDown={handleNumberOfWeekdaysInputKeyDown}
                onValueChange={handleInputNumberOfWeekdaysChange}
                ref={weekdaysInputRef}
                textAlign="right"
                value={inputNumberOfWeekdays}
                width="64px"
              />
              <Text>{weekdaysLabel}</Text>
            </Flex>
          ) : (
            <Button
              disabled={isNumberOfWeekdaysOutOfRange}
              icon={<Icon.FaCalendarEdit />}
              onClick={handleEditWeekdaysClicked}
              ref={weekdaysButtonRef}
              type="link"
            >
              {weekdaysButtonLabel}
            </Button>
          )}
        </Flex>
      </DatePickerPopoverContent>
    </Popover>
  )
}

export default React.memo(DatePicker)

/**
 * @description Estimates the width of the internal input value text with a hidden span. The input value is rendered in the shadow DOM and is otherwise inaccessible.
 */
const createTextWidthSpan = (input: HTMLInputElement) => {
  const computedStyles = window.getComputedStyle(input)

  // Create a span in memory
  const span = document.createElement('span')

  // Copy only relevant text styles
  const styleProps: Extract<
    keyof HTMLInputElement['style'],
    | 'font'
    | 'fontSize'
    | 'fontFamily'
    | 'fontWeight'
    | 'fontStyle'
    | 'letterSpacing'
    | 'textTransform'
    | 'textIndent'
    | 'textDecoration'
    | 'whiteSpace'
    | 'wordSpacing'
    | 'lineHeight'
  >[] = [
    'font',
    'fontSize',
    'fontFamily',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'textTransform',
    'textIndent',
    'textDecoration',
    'whiteSpace',
    'wordSpacing',
    'lineHeight',
  ]

  styleProps.forEach((prop) => {
    span.style[prop] = computedStyles[prop]
  })

  // Set the text content to the input's value
  span.textContent = input.value || input.placeholder || ''

  // Avoid line breaks and make sure it's hidden from view
  span.style.position = 'absolute'
  span.style.visibility = 'hidden'
  span.style.whiteSpace = 'pre'

  // Append to body for layout measuring
  document.body.appendChild(span)

  return span
}
